import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type OktaAuth from '@okta/okta-auth-js';
import { AccessToken, UserClaims } from '@okta/okta-auth-js';
import { from, merge, switchMap } from 'rxjs';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthContextService } from '@core/auth/context/auth-context.service';
import { OKTA_NO_RENEW } from '@core/auth/okta-auth.constants';

export type UserContext =
  | { type: 'internal'; claims: UserClaims }
  | { type: 'external'; claims: UserClaims; sfContactId: string | null }
  | { type: 'none' };

@Injectable({
  providedIn: 'root',
})
export class SessionStore {
  private readonly authContext = inject(AuthContextService);
  private readonly oktaAuthState = inject(OktaAuthStateService);
  private readonly internalOktaAuth = inject<OktaAuth>(OKTA_AUTH);

  readonly context = toSignal(
    merge(this.oktaAuthState.authState$, this.authContext.authChanges$).pipe(
      switchMap(() => from(this.resolveContext())),
    ),
    { initialValue: { type: 'none' } as UserContext },
  );

  async isAuthenticated(): Promise<boolean> {
    const storedAuthType = this.authContext.getStoredAuthType();

    if (storedAuthType === 'external') {
      return this.hasValidExternalSession();
    }

    if (storedAuthType === 'internal') {
      return this.hasValidInternalSession();
    }

    // hc_auth_type missing (e.g. new tab): restore from valid tokens only — never
    // default to internal renew/SSO probe (that path hangs and blanks the UI).
    if (await this.hasValidInternalSession()) {
      this.authContext.setAuthType('internal');
      return true;
    }

    if (await this.hasValidExternalSession()) {
      this.authContext.setAuthType('external');
      return true;
    }

    return false;
  }

  private async hasValidInternalSession(): Promise<boolean> {
    return this.internalOktaAuth.isAuthenticated(OKTA_NO_RENEW);
  }

  private async hasValidExternalSession(): Promise<boolean> {
    const externalAuth = this.authContext.getExternalOktaAuthIfAvailable();
    return !!(externalAuth && (await externalAuth.isAuthenticated(OKTA_NO_RENEW)));
  }

  private async resolveContext(): Promise<UserContext> {
    const activeAuth = this.authContext.getActiveOktaAuth();
    const isAuthenticated = await activeAuth.isAuthenticated(OKTA_NO_RENEW);

    if (!isAuthenticated) {
      return { type: 'none' };
    }

    const authType = this.authContext.getAuthType();
    const claims = await activeAuth.getUser();

    if (!claims) {
      return { type: 'none' };
    }

    if (authType === 'internal') {
      return { type: 'internal', claims };
    }

    const accessToken = (await activeAuth.tokenManager.get(
      'accessToken',
    )) as AccessToken | undefined;

    const sfContactId =
      (accessToken?.claims?.['sfContactId'] as string | undefined) ?? null;

    return { type: 'external', claims, sfContactId };
  }
}
